// Bileli · modules/media/store — LIVE (in-memory) upload state on zustand/vanilla, rebuilt each session by
// the engine: resumingStore = byte-upload/resume % per assetId; finalizeProgressStore = Bunny encode %
// (MONOTONIC — Bunny's status is jumpy, never let the bar go backwards); plus a claim registry so a
// mounted editor pane and the global finalize driver never double-poll the same asset.

import { createStore } from '../../../shared/lib/zustandTeact';

export type PctMap = Record<string, number>;

export const resumingStore = createStore<PctMap>(() => ({}));
export const finalizeProgressStore = createStore<PctMap>(() => ({}));

export function startResume(assetId: string): void {
  resumingStore.setState((m) => ({ ...m, [assetId]: m[assetId] ?? 0 }), true);
}
export function setResumePct(assetId: string, pct: number): void {
  resumingStore.setState((m) => ({ ...m, [assetId]: pct }), true);
}
export function finishResume(assetId: string): void {
  resumingStore.setState((m) => {
    const { [assetId]: _, ...rest } = m;
    return rest;
  }, true);
}
export function isResuming(assetId: string): boolean {
  return assetId in resumingStore.getState();
}

export function setEncodePct(assetId: string, pct: number): void {
  finalizeProgressStore.setState((m) => ({ ...m, [assetId]: Math.max(m[assetId] ?? 0, pct) }), true);
}

// Bileli 2026-07-29 — QUEUED is a distinct state from "encoding at 0%".
// Bunny's library sits on the free shared encoder (a settled decision, not a temporary condition), so a
// video waits 10–17 minutes in a queue before any transcoding starts. Throughout that wait Bunny reports
// progress 0, and the backend now SUPPRESSES it to null and sends `queued: true` instead — because a
// rendered "0%" reports a stall that is not happening, and that single number is most of why a slow
// upload read as a broken one. Kept as its own store rather than folded into the pct map so the monotonic
// guard above stays a plain number map.
// ⚠ NEVER derive this from `bunnyStatus`: verified in production 2026-07-29, a video went status 2 @ 5%
// straight to status 4 without ever reporting 3. Bunny does real encoding work under status 2, so the
// status cannot separate the two waits — only the server's flag can.
export type QueuedMap = Record<string, boolean>;
export const encodeQueuedStore = createStore<QueuedMap>(() => ({}));

export function setEncodeQueued(assetId: string, queued: boolean): void {
  encodeQueuedStore.setState((m) => (m[assetId] === queued ? m : { ...m, [assetId]: queued }), true);
}

export function finishEncode(assetId: string): void {
  finalizeProgressStore.setState((m) => {
    const { [assetId]: _, ...rest } = m;
    return rest;
  }, true);
  encodeQueuedStore.setState((m) => {
    const { [assetId]: _, ...rest } = m;
    return rest;
  }, true);
}

// ── claim registry (plain Set, non-reactive) — DORMANT, kept deliberately ──
//
// Bileli 2026-07-29: commented out, not deleted. This registry arbitrates between TWO producers of the
// encode percentage. In frontend/web a mounted editor pane polled finalize itself and pushed the % into
// local state, and `claimFinalize` kept the global driver off that same asset so they could not
// double-poll. The port to web-a brought these functions but NOT the second producer, so there has only
// ever been one: the global engine. `isFinalizeClaimed` could therefore only ever return false, and a
// guard that cannot fire is worse than no guard — it reads as protection that isn't there.
//
// Uncomment all four (and the scanFinalize guard in engine/uploadEngine.ts) the day a per-pane poller is
// added. Until then the engine is the single producer by design.
//
// const claimed = new Set<string>();
// export function claimFinalize(assetId: string): void {
//   claimed.add(assetId);
// }
// export function releaseFinalize(assetId: string): void {
//   claimed.delete(assetId);
// }
// export function isFinalizeClaimed(assetId: string): boolean {
//   return claimed.has(assetId);
// }
